import { createSelector } from 'reselect';

import { GlobalState } from 'ha/types/store';
import {
  DateFilterValue,
  PriceFilterValue,
  TypeFilterValue,
  SearchAreaFilterValue,
  SearchAreaValue,
} from 'ha/types/SearchFilters';
import { getFeatureFlags } from 'ha/modules/FeatureFlags';

import { getUseMapAsAFilter, isInitialState, getBounds } from './mapSelectors';

export const getIsContractTypeFilterHidden = (state: GlobalState) =>
  state.search.contractTypeFilter.isHidden;

export const getIsFurnitureFilterHidden = (state: GlobalState) =>
  state.search.furnitureFilter.isHidden;

export const getIsGenderFilterHidden = (state: GlobalState) =>
  state.search.genderFilter.isHidden;

export const getIsRegistrationFilterHidden = (state: GlobalState) =>
  state.search.registrationFilter.isHidden;

export const getIsSuitableForFilterHidden = (state: GlobalState) =>
  state.search.suitableForFilter.isHidden;

export const getIsFacilitiesFilterHidden = (state: GlobalState) =>
  state.search.filters.facilities.isHidden;

export const getIsAmenitiesFilterHidden = (state: GlobalState) =>
  state.search.filters.amenities.isHidden;

export const getIsSearchAreaFilterHidden = createSelector(
  [isInitialState, getBounds, getFeatureFlags],
  (initialState, bounds, featureFlag) =>
    featureFlag.searchLocationFilters !== 'on' &&
    (featureFlag.searchUseMapAsAFilter !== 'on' ||
      !bounds ||
      (bounds && initialState)),
);
export const getDateFilter = (state: GlobalState) => state.search.dateFilter;

export const getDateFilterValue = createSelector(
  getDateFilter,
  (dateFilter): DateFilterValue => ({
    startDate: dateFilter.startDate || null,
    endDate: dateFilter.endDate || null,
  }),
);

export const getCollection = (state: GlobalState) =>
  state.search.collectionFilter.collection;
export const getCollectionListingCount = (state: GlobalState) =>
  state.search.collectionFilter.listingCount;

export const getTypeFilterValue = (state: GlobalState): TypeFilterValue =>
  state.search.collectionFilter.categories;

export const getFurnitureFilter = (state: GlobalState) =>
  state.search.furnitureFilter.value;
export const getFurnitureListingCount = (state: GlobalState) =>
  state.search.furnitureFilter.listingCount;

export const getBillsFilter = (state: GlobalState) =>
  state.search.billsFilter.value;
export const getBillsListingCount = (state: GlobalState) =>
  state.search.billsFilter.listingCount;

export const getRoomsFilter = (state: GlobalState) => state.search.roomsFilter;
export const getRoomsListingCount = (state: GlobalState) =>
  state.search.roomsFilter.listingCount;

export const getRulesFilter = (state: GlobalState) =>
  state.search.rulesFilter.value;

export const getFacilitiesFilter = (state: GlobalState) =>
  state.search.filters.facilities.value;
export const getFacilitiesListingCount = (state: GlobalState) =>
  state.search.filters.facilities.listingCount;

export const getAmenitiesFilter = (state: GlobalState) =>
  state.search.filters.amenities.value;
export const getAmenitiesListingCount = (state: GlobalState) =>
  state.search.filters.amenities.listingCount;

export const getPriceFilter = (state: GlobalState) => state.search.priceFilter;

export const getPriceFilterValue = createSelector(
  getPriceFilter,
  (priceFilter): PriceFilterValue => ({
    min: priceFilter.value.min,
    max: priceFilter.value.max,
    currency: priceFilter.currency,
  }),
);

export const getRegistrationFilter = (state: GlobalState) =>
  state.search.registrationFilter.value;
export const getRegistrationListingCount = (state: GlobalState) =>
  state.search.registrationFilter.listingCount;

export const getRecentlyAddedFilter = (state: GlobalState) =>
  state.search.recentlyAddedFilter;

export const getGenderFilter = (state: GlobalState) =>
  state.search.genderFilter.value;
export const getGenderListingCount = (state: GlobalState) =>
  state.search.genderFilter.listingCount;

export const getSuitableForFilter = (state: GlobalState) =>
  state.search.suitableForFilter.value;
export const getSuitableForListingCount = (state: GlobalState) =>
  state.search.suitableForFilter.listingCount;

export const getContractTypeFilter = (state: GlobalState) =>
  state.search.contractTypeFilter.value;
export const getContractTypeListingCount = (state: GlobalState) =>
  state.search.contractTypeFilter.listingCount;

export const getSearchAreaFilterValue = createSelector(
  getUseMapAsAFilter,
  (useMapAsAFilter): SearchAreaFilterValue => [
    useMapAsAFilter ? SearchAreaValue.MapBounds : SearchAreaValue.CityBounds,
  ],
);
export const getPropertySizeFilter = (state: GlobalState) =>
  state.search.filters.propertySize.value;
export const getPropertySizeListingCount = (state: GlobalState) =>
  state.search.filters.propertySize.listingCount;
export const getPropertySizeStats = (state: GlobalState) =>
  state.search.filters.propertySize.stats;

export const getRadiusFilter = (state: GlobalState) =>
  state.search.filters.radius.value;

export const getSortingFilter = (state: GlobalState) =>
  state.search.sorting.currentSorting;

// typings for createSelector only allow a limited number of selectors
const getFilterValuesTemp = createSelector(
  [
    getDateFilterValue,
    getTypeFilterValue,
    getFurnitureFilter,
    getBillsFilter,
    getRoomsFilter,
    getRulesFilter,
    getPriceFilterValue,
    getRegistrationFilter,
    getRecentlyAddedFilter,
    getGenderFilter,
    getSuitableForFilter,
  ],
  (
    dates,
    types,
    furniture,
    bills,
    rooms,
    rules,
    price,
    registration,
    recentlyAdded,
    gender,
    suitableFor,
    // eslint-disable-next-line max-params
  ) => ({
    dates,
    types,
    furniture,
    bills,
    rooms,
    rules,
    price,
    registration,
    recentlyAdded,
    gender,
    suitableFor,
  }),
);

export const getFilterValues = createSelector(
  [
    getFilterValuesTemp,
    getContractTypeFilter,
    getSearchAreaFilterValue,
    getPropertySizeFilter,
    getFacilitiesFilter,
    getAmenitiesFilter,
    getRadiusFilter,
  ],
  (
    filterValues,
    contractType,
    searchArea,
    propertySize,
    facilities,
    amenities,
    radius,
  ) => ({
    ...filterValues,
    contractType,
    searchArea,
    propertySize,
    facilities,
    amenities,
    radius,
  }),
);

export const getFiltersCount = createSelector(
  [
    getFurnitureListingCount,
    getSuitableForListingCount,
    getGenderListingCount,
    getRegistrationListingCount,
    getBillsListingCount,
    getCollectionListingCount,
    getContractTypeListingCount,
    getPropertySizeListingCount,
    getFacilitiesListingCount,
    getAmenitiesListingCount,
    getRoomsListingCount,
  ],
  (
    furniture,
    suitableFor,
    gender,
    registration,
    bills,
    propertyType,
    contractType,
    propertySize,
    facilities,
    amenities,
    rooms,
    // eslint-disable-next-line max-params
  ) => ({
    furniture,
    suitableFor,
    gender,
    registration,
    bills,
    propertyType,
    contractType,
    propertySize,
    facilities,
    amenities,
    rooms,
  }),
);

export const getNoFilterFacet = (state: GlobalState) =>
  state.search.filters.noFiltersFacet;
